import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Container, Paper, ThemeIcon, Group, Stack, Grid,
  Button, Divider, Box, TextInput, Select, NumberInput, FileInput,
  Card, Badge, Avatar, ActionIcon, Tabs, Table, Modal, ScrollArea,
  Center
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuildingWarehouse, IconBarcode, IconPlus, IconSearch,
  IconFileInvoice, IconArrowLeft, IconAlertCircle, IconCheck,
  IconList, IconHistory, IconScan, IconEye, IconPrinter, IconDownload,
  IconPackage
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

export default function WarehouseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [warehouseName, setWarehouseName] = useState('Depo Detayları');
  const [activeTab, setActiveTab] = useState('operation');

  // Listeleme State'leri
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  // Modal State'leri
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [productModalOpened, { open: openProductModal, close: closeProductModal }] = useDisclosure(false);
  const [txModalOpened, { open: openTxModal, close: closeTxModal }] = useDisclosure(false);

  // Operasyon State'leri (Stok Kartı Ekleme)
  const [newCardBarcode, setNewCardBarcode] = useState('');
  const [newCardName, setNewCardName] = useState('');
  const [newCardCategory, setNewCardCategory] = useState('');
  const [newCardUnit, setNewCardUnit] = useState('Adet');
  const [newCardFile, setNewCardFile] = useState(null);
  const [cardSubmitting, setCardSubmitting] = useState(false);

  // Operasyon State'leri (Barkod & Stok Hareketi)
  const [searchBarcode, setSearchBarcode] = useState('');
  const [scannedProduct, setScannedProduct] = useState(null);
  const [txType, setTxType] = useState('GİRİŞ');
  const [txQuantity, setTxQuantity] = useState(1);
  const [txReceivedBy, setTxReceivedBy] = useState('');
  const [txReason, setTxReason] = useState('');
  const [txWaybillNo, setTxWaybillNo] = useState('');
  const [txFile, setTxFile] = useState(null);
  const [txSubmitting, setTxSubmitting] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    try {
      const [whRes, prodRes, txRes] = await Promise.all([
        api.get('/warehouse/warehouses/all'),
        api.get('/warehouse/products/all'),
        api.get('/warehouse/transactions/all')
      ]);

      // URL'den ID gelmediyse 1 numaralı depoyu (varsayılan) baz al
      const safeWarehouseId = parseInt(id) || 1;

      // Depo adını bul
      const currentWh = whRes.data.find(w => w.id === safeWarehouseId);
      if (currentWh) setWarehouseName(currentWh.name);

      setProducts(prodRes.data || []);
      
      // Kaydedilen hareketleri güvenli ID'ye göre filtrele
      const whTransactions = (txRes.data || []).filter(t => t.warehouse_id === safeWarehouseId);
      setTransactions(whTransactions);

      generateBarcodeForForm();
    } catch (err) {
      notifications.show({ title: 'Hata', message: 'Veriler yüklenemedi.', color: 'red' });
    }
  };

  const generateBarcodeForForm = async () => {
    try {
      const res = await api.get('/warehouse/products/generate-barcode');
      setNewCardBarcode(res.data.barcode);
    } catch (err) {
      setNewCardBarcode('');
    }
  };

  // Stok listesinde anlık mevcut adedi hesaplamak için yardımcı fonksiyon
  const getProductStock = (prodId) => {
    const prodTxs = transactions.filter(t => t.product_id === prodId);
    let currentStock = 0;
    prodTxs.forEach(tx => {
      currentStock += tx.transaction_type === 'GİRİŞ' ? tx.quantity : -tx.quantity;
    });
    return currentStock > 0 ? currentStock : 0;
  };

  // --- STOK KARTI OLUŞTURMA FONKSİYONU ---
  const handleCreateStockCard = async (e) => {
    e.preventDefault();
    setCardSubmitting(true);

    const formData = new FormData();
    formData.append('barcode', newCardBarcode);
    formData.append('name', newCardName);
    formData.append('category', newCardCategory);
    formData.append('unit', newCardUnit);
    if (newCardFile) {
      formData.append('files', newCardFile);
    }

    try {
      await api.post('/warehouse/products/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      notifications.show({
        title: 'Başarılı',
        message: 'Stok kartı ve ürün görseli başarıyla kaydedildi.',
        color: 'green',
        icon: <IconCheck size={16} />
      });
      
      setNewCardName('');
      setNewCardCategory('');
      setNewCardFile(null);
      await fetchInitialData(); // Listeleri güncelle ve yeni barkod al
    } catch (err) {
      notifications.show({
        title: 'Hata',
        message: err.response?.data?.detail || 'Stok kartı oluşturulamadı.',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setCardSubmitting(false);
    }
  };

  // --- BARKOD SORGULAMA (Manuel veya Okuyucu ile) ---
  const triggerBarcodeSearch = async () => {
    if (!searchBarcode.trim()) return;
    try {
      const res = await api.get(`/warehouse/stock/${searchBarcode.trim()}`);
      setScannedProduct(res.data);
      notifications.show({
        title: 'Ürün Bulundu',
        message: `${res.data.product.name} kartı başarıyla getirildi.`,
        color: 'blue',
        icon: <IconCheck size={16} />
      });
    } catch (err) {
      setScannedProduct(null);
      notifications.show({
        title: 'Bulunamadı',
        message: 'Bu barkoda ait bir ürün sistemde kayıtlı değil.',
        color: 'orange',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      triggerBarcodeSearch();
    }
  };

  const handleManualProductSelect = async (barcodeVal) => {
    setSearchBarcode(barcodeVal || '');
    if (!barcodeVal) {
      setScannedProduct(null);
      return;
    }
    try {
      const res = await api.get(`/warehouse/stock/${barcodeVal}`);
      setScannedProduct(res.data);
    } catch (err) {
      setScannedProduct(null);
    }
  };

  // --- STOK HAREKETİ KAYDI ---
  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!scannedProduct) return;
    setTxSubmitting(true);

    const formData = new FormData();
    formData.append('product_id', Number(scannedProduct.product.id));
    const safeWarehouseId = parseInt(id) || 1; 
    formData.append('warehouse_id', safeWarehouseId);
    formData.append('transaction_type', txType);
    formData.append('quantity', Number(txQuantity));
    formData.append('performed_by', 'Sistem Sorumlusu');

    // Opsiyonel alanları sadece doluysa ekliyoruz
    if (txWaybillNo) formData.append('waybill_no', txWaybillNo);

    if (txType === 'ÇIKIŞ') {
      formData.append('received_by', txReceivedBy);
      formData.append('reason', txReason);
    }

    if (txFile) {
      formData.append('files', txFile);
      formData.append('doc_types', txType === 'GİRİŞ' ? 'İrsaliye Evrağı' : 'Zimmet Tutanağı');
    }

    try {
      await api.post('/warehouse/transactions/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      notifications.show({
        title: 'İşlem Başarılı',
        message: `Stok ${txType.toLowerCase()} kaydı başarıyla oluşturuldu.`,
        color: 'green',
        icon: <IconCheck size={16} />
      });
      
      const updatedProduct = await api.get(`/warehouse/stock/${scannedProduct.product.barcode}`);
      setScannedProduct(updatedProduct.data);
      
      setTxQuantity(1);
      setTxReceivedBy('');
      setTxReason('');
      setTxWaybillNo('');
      setTxFile(null);
      await fetchInitialData(); 
    } catch (err) {
      notifications.show({
        title: 'İşlem Başarısız',
        message: 'Stok hareketi veritabanına işlenemedi.',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setTxSubmitting(false);
    }
  };

  // --- BARKOD YAZDIRMA FONKSİYONU ---
  const handlePrintBarcode = (product) => {
    const printWindow = window.open('', '_blank', 'width=600,height=400');
    printWindow.document.write(`
      <html>
        <head>
          <title>Barkod Yazdır - ${product.barcode}</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding-top: 50px; }
            .label { border: 2px solid #000; display: inline-block; padding: 20px; border-radius: 8px; }
            h2 { margin: 0 0 10px 0; font-size: 20px; }
            p { margin: 10px 0 0 0; font-size: 14px; font-weight: bold; }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        </head>
        <body>
          <div class="label">
            <h2>${product.name}</h2>
            <svg id="barcode"></svg>
            <p>Kategori: ${product.category || 'Genel'}</p>
          </div>
          <script>
            window.onload = function() {
              JsBarcode("#barcode", "${product.barcode}", {
                format: "CODE128",
                width: 2,
                height: 60,
                displayValue: true,
                fontSize: 16
              });
              setTimeout(() => { window.print(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const viewProductDetails = (product) => {
    setSelectedProduct(product);
    openProductModal();
  };

  const viewTxDetails = (tx) => {
    setSelectedTx(tx);
    openTxModal();
  };

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="lg">
        
        {/* ÜST GEZİNTİ VE BAŞLIK */}
        <Group justify="space-between">
          <Button variant="subtle" color="gray" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/warehouse')}>
            Panele Geri Dön
          </Button>
          <Group gap="xs">
            <ThemeIcon color="blue" size="md" radius="xl"><IconBuildingWarehouse size={18} /></ThemeIcon>
            <Title order={3} fw={800}>{warehouseName} Yönetimi</Title>
          </Group>
        </Group>

        <Paper radius="xl" p="md" withBorder shadow="sm">
          <Tabs value={activeTab} onChange={setActiveTab} color="blue" variant="pills" radius="xl">
            <Tabs.List mb="lg">
              <Tabs.Tab value="operation" leftSection={<IconScan size={16} />}>Operasyon Merkezi</Tabs.Tab>
              <Tabs.Tab value="inventory" leftSection={<IconList size={16} />}>Stok Envanteri</Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>İşlem Geçmişi</Tabs.Tab>
            </Tabs.List>

            {/* SEKME 1: OPERASYON MERKEZİ */}
            <Tabs.Panel value="operation">
              <Grid columns={12} gutter="lg">
                
                {/* SOL TARAF: Yeni Stok Kartı Formu */}
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <Paper radius="lg" p="xl" bg="gray.0" withBorder h="100%">
                    <Group gap="xs" mb="sm">
                      <ThemeIcon variant="light" color="blue" radius="md"><IconPlus size={18} /></ThemeIcon>
                      <Text fw={700} size="lg">Hızlı Stok Kartı Tanımlama</Text>
                    </Group>
                    <Text size="xs" c="dimmed" mb="lg">Sisteme ilk defa girecek malzemeler için benzersiz barkodlu kart tanımlayın.</Text>
                    <Divider mb="xl" />

                    <form onSubmit={handleCreateStockCard}>
                      <Stack gap="md">
                        <TextInput
                          label="Barkod (Otomatik Üretildi)"
                          value={newCardBarcode}
                          readOnly
                          disabled
                          variant="filled"
                          rightSection={
                            <ActionIcon variant="subtle" color="gray" onClick={generateBarcodeForForm}>
                              <IconBarcode size={16} />
                            </ActionIcon>
                          }
                        />
                        <TextInput
                          label="Malzeme / Ürün Adı"
                          placeholder="Örn: Lenovo ThinkPad L14"
                          required
                          value={newCardName}
                          onChange={(e) => setNewCardName(e.currentTarget.value)}
                        />
                        <TextInput
                          label="Kategori / Sınıf"
                          placeholder="Örn: Bilgi İşlem Donanım"
                          value={newCardCategory}
                          onChange={(e) => setNewCardCategory(e.currentTarget.value)}
                        />
                        <Select
                          label="Ölçü Birimi"
                          data={['Adet', 'Metre', 'Koli', 'Paket', 'Kg']}
                          value={newCardUnit}
                          onChange={setNewCardUnit}
                        />
                        <FileInput
                          label="Ürün Görseli"
                          placeholder="Dosya seçin veya sürükleyin"
                          accept="image/*"
                          value={newCardFile}
                          onChange={setNewCardFile}
                        />
                        <Button type="submit" color="blue" radius="xl" fullWidth mt="md" loading={cardSubmitting}>
                          Stok Kartını Sisteme Kaydet
                        </Button>
                      </Stack>
                    </form>
                  </Paper>
                </Grid.Col>

                {/* SAĞ TARAF: Barkod Okutma ve Hareket Formu */}
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Paper radius="lg" p="xl" bg="gray.0" withBorder h="100%">
                    <Title order={5} mb="md">Stok Giriş / Çıkış İşlemleri</Title>
                    <Stack gap="lg">
                      
                      {/* BARKOD ARAMA VE SEÇME ALANI */}
                      <Stack gap="sm">
                        <Group align="flex-end">
                          <TextInput
                            style={{ flex: 1 }}
                            size="md"
                            radius="md"
                            autoFocus
                            placeholder="Tarayıcı ile okutun veya yazıp Enter'a basın..."
                            label="Barkod ile Bul"
                            leftSection={<IconSearch size={18} />}
                            value={searchBarcode}
                            onChange={(e) => setSearchBarcode(e.currentTarget.value)}
                            onKeyDown={handleBarcodeKeyDown}
                          />
                          <Button size="md" radius="md" onClick={triggerBarcodeSearch}>
                            Sorgula
                          </Button>
                        </Group>

                        <Divider my="xs" label="veya manuel ürün seçin" labelPosition="center" />

                        <Select
                          label="Kayıtlı Ürünlerden Seç"
                          placeholder="Listeden malzeme adına veya barkoduna göre ara..."
                          data={products.map(p => ({ value: p.barcode, label: `${p.name} (${p.barcode})` }))}
                          searchable
                          clearable
                          value={scannedProduct?.product?.barcode || null}
                          onChange={handleManualProductSelect}
                        />
                      </Stack>

                      {/* SORGULANAN ÜRÜN BULUNDUYSA İŞLEM FORMU AÇILIR */}
                      {scannedProduct ? (
                        <Paper radius="md" p="md" withBorder>
                          {/* Ürün Bilgi Kartı */}
                          <Card withBorder radius="md" p="md" mb="xl" bg="white">
                            <Group justify="space-between" align="center">
                              <Group gap="sm">
                                <Avatar 
                                  src={scannedProduct.product.documents?.[0]?.url ? `${api.defaults.baseURL}${scannedProduct.product.documents[0].url}` : null} 
                                  radius="md" 
                                  size="lg"
                                  color="blue"
                                >
                                  <IconPackage size={24} />
                                </Avatar>
                                <Box>
                                  <Text fw={700} size="md">{scannedProduct.product.name}</Text>
                                  <Text size="xs" c="dimmed">Barkod: {scannedProduct.product.barcode} | Kategori: {scannedProduct.product.category || 'Belirtilmemiş'}</Text>
                                </Box>
                              </Group>
                              <Stack gap={2} align="flex-end">
                                <Text size="xs" c="dimmed" fw={700}>Mevcut Envanter</Text>
                                <Badge size="lg" color="green" variant="filled">
                                  {getProductStock(scannedProduct.product.id)} {scannedProduct.product.unit}
                                </Badge>
                              </Stack>
                            </Group>
                          </Card>

                          {/* Stok Giriş/Çıkış Formu */}
                          <form onSubmit={handleTransactionSubmit}>
                            <Stack gap="md">
                              <Group grow>
                                <Select
                                  label="İşlem Türü"
                                  data={[
                                    { value: 'GİRİŞ', label: 'Mal Kabul / Stok Girişi' },
                                    { value: 'ÇIKIŞ', label: 'Zimmet / Stok Çıkışı' }
                                  ]}
                                  value={txType}
                                  onChange={(val) => setTxType(val || 'GİRİŞ')}
                                />
                                <NumberInput
                                  label="İşlem Miktarı"
                                  min={1}
                                  value={txQuantity}
                                  onChange={(val) => setTxQuantity(Number(val || 1))}
                                />
                              </Group>

                              <TextInput
                                label="İrsaliye / Fiş No / Firma Adı"
                                placeholder="Örn: IRS20240001243 veya Firma Adı"
                                value={txWaybillNo}
                                onChange={(e) => setTxWaybillNo(e.currentTarget.value)}
                                required={txType === 'GİRİŞ'} 
                              />

                              {/* ÇIKIŞ İşleminde Gözüken Ek Alanlar */}
                              {txType === 'ÇIKIŞ' && (
                                <Group grow>
                                  <TextInput
                                    label="Zimmet Edilen Kişi / Birim"
                                    placeholder="Kime teslim edildi?"
                                    required
                                    value={txReceivedBy}
                                    onChange={(e) => setTxReceivedBy(e.currentTarget.value)}
                                  />
                                  <TextInput
                                    label="Çıkış Gerekçesi / Nedeni"
                                    placeholder="Örn: Yeni Başlayan Personel"
                                    required
                                    value={txReason}
                                    onChange={(e) => setTxReason(e.currentTarget.value)}
                                  />
                                </Group>
                              )}

                              <FileInput
                                label={txType === 'GİRİŞ' ? 'İrsaliye Belgesi (PDF/Görsel)' : 'Zimmet / Teslim Tutanağı'}
                                placeholder="Evrak yüklemek için tıklayın"
                                leftSection={<IconFileInvoice size={16} />}
                                value={txFile}
                                onChange={setTxFile}
                              />

                              <Button 
                                type="submit" 
                                color={txType === 'GİRİŞ' ? 'green' : 'red'} 
                                radius="xl" 
                                fullWidth 
                                mt="md"
                                loading={txSubmitting}
                              >
                                Stok {txType === 'GİRİŞ' ? 'Girişini' : 'Çıkışını'} Onayla ve Deftere İşle
                              </Button>
                            </Stack>
                          </form>
                        </Paper>
                      ) : (
                        <Paper radius="md" p="xl" withBorder h={250}>
                          <Center h="100%">
                            <Stack align="center" gap="xs" c="dimmed">
                              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                                <IconBarcode size={26} />
                              </ThemeIcon>
                              <Text size="sm" fw={600}>İşlem Yapılacak Ürünü Seçin</Text>
                              <Text size="xs" ta="center" maw={350}>
                                Stok girmek veya çıkmak için yukarıdan ürün barkodunu sorgulayın ya da listeden manuel olarak seçin.
                              </Text>
                            </Stack>
                          </Center>
                        </Paper>
                      )}
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            {/* SEKME 2: STOK ENVANTERİ LİSTESİ */}
            <Tabs.Panel value="inventory">
              <ScrollArea>
                <Table verticalSpacing="md" highlightOnHover striped>
                  <Table.Thead bg="gray.1">
                    <Table.Tr>
                      <Table.Th>Görsel</Table.Th>
                      <Table.Th>Barkod</Table.Th>
                      <Table.Th>Malzeme Adı</Table.Th>
                      <Table.Th>Kategori</Table.Th>
                      <Table.Th>Mevcut Stok</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {products.map((prod) => (
                      <Table.Tr key={prod.id}>
                        <Table.Td>
                          <Avatar src={prod.documents?.[0]?.url ? `${api.defaults.baseURL}${prod.documents[0].url}` : null} radius="sm" />
                        </Table.Td>
                        <Table.Td><Badge variant="outline" color="dark">{prod.barcode}</Badge></Table.Td>
                        <Table.Td fw={600}>{prod.name}</Table.Td>
                        <Table.Td>{prod.category || '-'}</Table.Td>
                        <Table.Td>
                          <Badge color="green" size="lg" variant="light">
                            {getProductStock(prod.id)} {prod.unit}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Group gap="xs" justify="flex-end">
                            <ActionIcon variant="light" color="blue" onClick={() => viewProductDetails(prod)}>
                              <IconEye size={18} />
                            </ActionIcon>
                            <ActionIcon variant="light" color="dark" onClick={() => handlePrintBarcode(prod)}>
                              <IconPrinter size={18} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Tabs.Panel>

            {/* SEKME 3: İŞLEM GEÇMİŞİ LİSTESİ */}
            <Tabs.Panel value="history">
              <ScrollArea>
                <Table verticalSpacing="md" highlightOnHover striped>
                  <Table.Thead bg="gray.1">
                    <Table.Tr>
                      <Table.Th>İşlem Tarihi</Table.Th>
                      <Table.Th>Ürün / Malzeme</Table.Th>
                      <Table.Th>Tür</Table.Th>
                      <Table.Th>Miktar</Table.Th>
                      <Table.Th>İrsaliye / Fiş</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Detay</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {transactions.map((tx) => (
                      <Table.Tr key={tx.id}>
                        <Table.Td>{new Date(tx.date).toLocaleString('tr-TR')}</Table.Td>
                        <Table.Td fw={600}>{tx.product?.name || 'Bilinmiyor'}</Table.Td>
                        <Table.Td>
                          <Badge color={tx.transaction_type === 'GİRİŞ' ? 'green' : 'red'}>
                            {tx.transaction_type}
                          </Badge>
                        </Table.Td>
                        <Table.Td fw={700}>{tx.quantity} {tx.product?.unit}</Table.Td>
                        <Table.Td>{tx.waybill_no || '-'}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Button variant="subtle" size="xs" onClick={() => viewTxDetails(tx)}>İncele</Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Stack>

      {/* --- ÜRÜN DETAY MODALI --- */}
      <Modal opened={productModalOpened} onClose={closeProductModal} title={<Title order={4}>Stok Kartı Detayı</Title>} size="lg" radius="md">
        {selectedProduct && (
          <Stack gap="md">
            <Group align="flex-start" gap="lg">
              <Avatar src={selectedProduct.documents?.[0]?.url ? `${api.defaults.baseURL}${selectedProduct.documents[0].url}` : null} size={120} radius="md" />
              <Box style={{ flex: 1 }}>
                <Text size="xl" fw={800}>{selectedProduct.name}</Text>
                <Badge mt="xs" mb="sm" color="dark">{selectedProduct.barcode}</Badge>
                <Grid columns={2}>
                  <Grid.Col span={1}><Text size="sm" c="dimmed">Kategori</Text><Text fw={600}>{selectedProduct.category || '-'}</Text></Grid.Col>
                  <Grid.Col span={1}><Text size="sm" c="dimmed">Ölçü Birimi</Text><Text fw={600}>{selectedProduct.unit}</Text></Grid.Col>
                </Grid>
              </Box>
            </Group>
            <Divider />
            <Paper p="md" withBorder bg="green.0" style={{ borderColor: 'var(--mantine-color-green-3)' }}>
              <Group justify="space-between" align="center">
                <Text fw={700} c="green.9">Depodaki Mevcut Stok:</Text>
                <Text fw={900} size="xl" c="green.9">{getProductStock(selectedProduct.id)} {selectedProduct.unit}</Text>
              </Group>
            </Paper>
            <Button leftSection={<IconPrinter size={16} />} color="dark" onClick={() => handlePrintBarcode(selectedProduct)}>
              Barkod Etiketi Yazdır
            </Button>
          </Stack>
        )}
      </Modal>

      {/* --- İŞLEM GEÇMİŞİ DETAY MODALI --- */}
      <Modal opened={txModalOpened} onClose={closeTxModal} title={<Title order={4}>Stok Hareketi Detayı</Title>} size="lg" radius="md">
        {selectedTx && (
          <Stack gap="sm">
            <Group justify="space-between">
              <Badge color={selectedTx.transaction_type === 'GİRİŞ' ? 'green' : 'red'} size="lg">{selectedTx.transaction_type}</Badge>
              <Text c="dimmed" size="sm">{new Date(selectedTx.date).toLocaleString('tr-TR')}</Text>
            </Group>
            <Divider my="sm" />
            <Grid>
              <Grid.Col span={6}><Text size="sm" c="dimmed">Ürün</Text><Text fw={600}>{selectedTx.product?.name}</Text></Grid.Col>
              <Grid.Col span={6}><Text size="sm" c="dimmed">Miktar</Text><Text fw={600}>{selectedTx.quantity} {selectedTx.product?.unit}</Text></Grid.Col>
              <Grid.Col span={6}><Text size="sm" c="dimmed">İrsaliye / Fiş No / Firma Adı</Text><Text fw={600}>{selectedTx.waybill_no || 'Belirtilmemiş'}</Text></Grid.Col>
              <Grid.Col span={6}><Text size="sm" c="dimmed">İşlemi Yapan</Text><Text fw={600}>{selectedTx.performed_by}</Text></Grid.Col>
              
              {selectedTx.transaction_type === 'ÇIKIŞ' && (
                <>
                  <Grid.Col span={6}><Text size="sm" c="dimmed">Teslim Alan</Text><Text fw={600}>{selectedTx.received_by}</Text></Grid.Col>
                  <Grid.Col span={6}><Text size="sm" c="dimmed">Çıkış Nedeni</Text><Text fw={600}>{selectedTx.reason}</Text></Grid.Col>
                </>
              )}
            </Grid>
            
            {/* Eğer Harekete Bağlı Evrak/Tutanak Varsa */}
            {selectedTx.documents?.length > 0 && (
              <>
                <Divider my="sm" />
                <Text fw={700} size="sm" mb="xs">Ekli Evraklar</Text>
                {selectedTx.documents.map(doc => (
                  <Button 
                    key={doc.id} 
                    variant="light" 
                    color="blue" 
                    leftSection={<IconDownload size={16} />}
                    component="a"
                    href={`${api.defaults.baseURL}${doc.url}`}
                    target="_blank"
                    download
                  >
                    {doc.type} İndir
                  </Button>
                ))}
              </>
            )}
          </Stack>
        )}
      </Modal>

    </Container>
  );
}